import { toast } from 'react-toastify';
import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { MdOutlineMap } from 'react-icons/md';
import EditPropertyModal from './EditPropertyModal';
import '@/styles/PropertyPagesModern.scss';
import * as Sentry from '@sentry/react';

/**
 * ViewProperties Component
 * 
 * Displays all properties in a modern, searchable table with enhanced UI/UX.
 * Features:
 * - Real-time search filtering
 * - Visual status indicators
 * - Responsive design
 * - Loading and empty states
 * - Modern card-based layout
 */
const ViewProperties = ({ refreshTrigger }) => {
    const { apiFetch } = useAuth();
    const navigate = useNavigate();
    const [properties, setProperties] = useState([]);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [showInactive, setShowInactive] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    /**
     * Fetches all properties from the API.
     */
    const fetchProperties = async () => {
        setIsLoading(true);
        try {
            const response = await apiFetch('/api/Property/all');
            const data = await response.json();
            if (data.success && data.data) {
                setProperties(data.data);
            } else {
                toast.error('Failed to load properties');
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error("Error fetching properties:", e);
            toast.error('Error loading properties');
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        fetchProperties();
    }, [refreshTrigger]);

    /**
     * Opens the edit modal for a specific property.
     */
    const handleEdit = (property) => {
        setSelectedProperty(property);
        setEditModalOpen(true);
    };

    /**
     * Closes the edit modal and refreshes the properties list.
     */
    const handleCloseModal = () => {
        setEditModalOpen(false);
        setSelectedProperty(null);
        fetchProperties(); 
    };

    /**
     * Filters properties based on search term.
     */
    const filteredProperties = properties
        .filter((prop) => {
            // Filter by active/inactive status
            const isActive = prop.active ?? prop.Active;
            if (!showInactive && !isActive) return false;

            const propertyName = prop.propertyName || prop.PropertyName || "";
            const comparativeName = prop.comparativeName || prop.ComparativeName || "";
            const shortCode = prop.shortCode || prop.ShortCode || "";
            const term = searchTerm.toLowerCase();
            return (
                propertyName.toLowerCase().includes(term) ||
                comparativeName.toLowerCase().includes(term) ||
                shortCode.toLowerCase().includes(term)
            );
        })
        .sort((a, b) => {
            const nameA = (a.propertyName || a.PropertyName || "").toLowerCase();
            const nameB = (b.propertyName || b.PropertyName || "").toLowerCase();
            return nameA.localeCompare(nameB);
        });

    return (
        <div className="view-properties-modern">
            {/* Search Bar and Filters */}
            <div className="search-filter-row">
                <div className="search-container">
                    <span className="search-icon">🔍</span>
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search by property name, comparative name, or short code..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        aria-label="Search properties"
                    />
                </div>
                <label className="inactive-toggle">
                    <input
                        type="checkbox"
                        checked={showInactive}
                        onChange={e => setShowInactive(e.target.checked)}
                    />
                    <span>Show Inactive Properties</span>
                </label>
            </div>

            {/* Properties Table */}
            {isLoading ? (
                <div className="loading-state">
                    <div className="spinner" aria-label="Loading properties"></div>
                    <p>Loading properties...</p>
                </div>
            ) : filteredProperties.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon">📋</div>
                    <h3>No Properties Found</h3>
                    <p>
                        {searchTerm 
                            ? "No properties match your search criteria." 
                            : "No properties available. Create a new property to get started."}
                    </p>
                </div>
            ) : (
                <div className="properties-table-container">
                    <table className="properties-table">
                        <thead>
                            <tr>
                                <th>Property Name</th>
                                <th>Comparative Name</th>
                                <th>Short Code</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProperties.map((prop) => (
                                <tr key={prop.propertyID || prop.PropertyID}>
                                    <td className="property-name">
                                        {prop.propertyName || prop.PropertyName}
                                    </td>
                                    <td className="comparative-name">
                                        {prop.comparativeName || prop.ComparativeName}
                                    </td>
                                    <td className="short-code">
                                        {prop.shortCode || prop.ShortCode || '-'}
                                    </td>
                                    <td>
                                        <span className={`status-badge ${(prop.active ?? prop.Active) ? 'active' : 'inactive'}`}>
                                            <span className="status-dot"></span>
                                            {(prop.active ?? prop.Active) ? "Active" : "Inactive"}
                                        </span>
                                    </td>
                                    <td className="actions-cell">
                                        <button 
                                            className="action-button floorplan-button"
                                            onClick={() => navigate(`/property/${prop.propertyID || prop.PropertyID}/floorplan`)}
                                            aria-label={`Floorplan for ${prop.propertyName || prop.PropertyName}`}
                                            title="Floorplan Designer"
                                        >
                                            <MdOutlineMap /> Floorplan
                                        </button>
                                        <button 
                                            className="action-button"
                                            onClick={() => handleEdit(prop)}
                                            aria-label={`Edit ${prop.propertyName || prop.PropertyName}`}
                                        >
                                            ✏️ Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Edit Modal */}
            {editModalOpen && (
                <EditPropertyModal
                    property={selectedProperty}
                    onClose={handleCloseModal}
                    fetchProperties={fetchProperties}
                />
            )}
</div>
    );
}

export default ViewProperties;